import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../utils/api';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

export default function ProfileSettings({ user, onUpdate }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    username: user.username || '',
    bio: user.bio || '',
    locationCity: user.locationCity || '',
    locationState: user.locationState || '',
    locationZip: user.locationZip || '',
    returnPolicy: user.returnPolicy || '',
    shippingPolicy: user.shippingPolicy || '',
  });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [vacation, setVacation] = useState({ mode: user.vacationMode || false, message: user.vacationMessage || '' });
  const [vacationLoading, setVacationLoading] = useState(false);
  const [vacationSuccess, setVacationSuccess] = useState('');
  const [bundle, setBundle] = useState({ minItems: 2, discountPercent: 10 });
  const [bundleActive, setBundleActive] = useState(false);
  const [bundleLoading, setBundleLoading] = useState(false);
  const [bundleSuccess, setBundleSuccess] = useState('');
  const [bannerUrl, setBannerUrl] = useState(user.sellerBannerUrl || user.seller_banner_url || '');
  const [bannerUploading, setBannerUploading] = useState(false);
  const [bannerMsg, setBannerMsg] = useState('');
  const [autoReply, setAutoReply] = useState({ enabled: user.autoReplyEnabled || false, message: user.autoReplyMessage || '' });
  const [autoReplyLoading, setAutoReplyLoading] = useState(false);
  const [autoReplySuccess, setAutoReplySuccess] = useState('');
  const [avatarUrl, setAvatarUrl] = useState(user.avatarUrl || user.avatar_url || '');
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarMsg, setAvatarMsg] = useState('');

  // Load bundle discount on mount
  useState(() => { API.get('/users/me/bundle-discount').then(r => { if (r.data.bundleDiscount) { setBundle({ minItems: r.data.bundleDiscount.min_items, discountPercent: r.data.bundleDiscount.discount_percent }); setBundleActive(true); } }).catch(()=>{}); });

  const validate = () => {
    const e = {};
    if (!form.firstName) e.firstName = 'Required';
    if (!form.lastName) e.lastName = 'Required';
    if (form.username && form.username.length < 3) e.username = 'Min 3 characters';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setSuccess('');
    try {
      await API.patch('/users/me', form);
      setSuccess('✅ Profile updated successfully');
      if (onUpdate) onUpdate(form);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setErrors({ submit: err.response?.data?.error || 'Failed to update profile' });
    } finally {
      setLoading(false);
    }
  };

  const f = (name) => ({
    className: `form-input ${errors[name] ? 'error' : ''}`,
    value: form[name],
    onChange: e => setForm(p => ({ ...p, [name]: e.target.value })),
  });

  const handleVacationToggle = async () => {
    setVacationLoading(true);
    setVacationSuccess('');
    try {
      const { data } = await API.patch('/users/me/vacation', { vacationMode: !vacation.mode, vacationMessage: vacation.message });
      setVacation(v => ({ ...v, mode: data.vacationMode }));
      setVacationSuccess(data.vacationMode ? '🏖️ Vacation mode on — all listings paused' : '✅ Back! All listings restored');
      setTimeout(() => setVacationSuccess(''), 4000);
    } catch (err) {
      setVacationSuccess('Failed to update vacation mode');
    } finally { setVacationLoading(false); }
  };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
        <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, fontWeight: 700 }}>👤 Profile Settings</h3>
        <a href={`/profile/${user?.id}`} style={{ fontSize:13, fontWeight:600, color:'var(--cherry)', textDecoration:'none', border:'1px solid var(--cherry)', borderRadius:8, padding:'5px 12px' }}>
          🏪 View My Storefront →
        </a>
      </div>

      <div style={{ background: 'white', borderRadius: 14, border: '1px solid var(--border)', padding: 24, maxWidth: 600 }}>
        {success && <div className="alert alert-success" style={{ marginBottom: 16 }}>{success}</div>}
        {errors.submit && <div className="alert alert-error" style={{ marginBottom: 16 }}>{errors.submit}</div>}

        {/* Avatar upload */}
        <div style={{ display:'flex', alignItems:'center', gap:20, marginBottom:24, paddingBottom:24, borderBottom:'1px solid var(--border)' }}>
          <div style={{ position:'relative', flexShrink:0 }}>
            <div style={{ width:80, height:80, borderRadius:'50%', overflow:'hidden', background:'#F3F4F6', border:'2px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:32, fontWeight:700, color:'var(--text-soft)' }}>
              {avatarUrl
                ? <img src={`${API_BASE}${avatarUrl}`} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                : (form.firstName?.[0] || '?').toUpperCase()
              }
            </div>
            <label style={{ position:'absolute', bottom:0, right:0, width:26, height:26, background:'var(--cherry)', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', border:'2px solid white', fontSize:13 }} title="Upload photo">
              {avatarUploading ? '⏳' : '📷'}
              <input type="file" accept="image/*" style={{ display:'none' }} onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                setAvatarUploading(true); setAvatarMsg('');
                try {
                  const fd = new FormData();
                  fd.append('avatar', file);
                  const { data } = await API.post('/upload/avatar', fd, { headers:{ 'Content-Type':'multipart/form-data' } });
                  setAvatarUrl(data.url);
                  setAvatarMsg('✅ Photo updated');
                } catch { setAvatarMsg('Upload failed'); }
                setAvatarUploading(false);
                setTimeout(() => setAvatarMsg(''), 3000);
              }} />
            </label>
          </div>
          <div>
            <div style={{ fontWeight:600, fontSize:14 }}>Profile Photo</div>
            <div style={{ fontSize:12, color:'var(--text-soft)', marginTop:2 }}>Shown on your storefront and listings</div>
            {avatarMsg && <div style={{ fontSize:12, color: avatarMsg.startsWith('✅') ? '#2D7A3A' : 'var(--cherry)', marginTop:4 }}>{avatarMsg}</div>}
          </div>
        </div>

        <form onSubmit={handleSave}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div className="form-group">
              <label className="form-label">First Name<span className="required"> *</span></label>
              <input {...f('firstName')} placeholder="John" />
              {errors.firstName && <div className="form-error">{errors.firstName}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Last Name<span className="required"> *</span></label>
              <input {...f('lastName')} placeholder="Smith" />
              {errors.lastName && <div className="form-error">{errors.lastName}</div>}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Username</label>
            <input {...f('username')} placeholder="johnsmith123" />
            <div style={{ fontSize: 12, color: 'var(--text-soft)', marginTop: 4 }}>Your unique identifier on Cherrypkd</div>
            {errors.username && <div className="form-error">{errors.username}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">Bio</label>
            <textarea
              {...f('bio')}
              placeholder="Tell others about yourself..."
              style={{ minHeight: 100, fontFamily: "'DM Sans',sans-serif", fontSize: 14, padding: 12, borderRadius: 8, border: '1px solid var(--border)', fontFamily: "'DM Sans',sans-serif" }}
            />
            <div style={{ fontSize: 12, color: 'var(--text-soft)', marginTop: 4 }}>Visible on your profile</div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div className="form-group">
              <label className="form-label">City</label>
              <input {...f('locationCity')} placeholder="Los Angeles" />
            </div>
            <div className="form-group">
              <label className="form-label">State</label>
              <input {...f('locationState')} placeholder="CA" />
            </div>
            <div className="form-group">
              <label className="form-label">Zip</label>
              <input {...f('locationZip')} placeholder="90001" />
            </div>
          </div>

          {/* Seller Policies */}
          <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>🏪 Storefront Policies <span style={{ fontWeight: 400, color: 'var(--text-soft)', fontSize: 12 }}>(shown on your public profile)</span></div>
            <div className="form-group">
              <label className="form-label">🚚 Shipping Policy</label>
              <textarea {...f('shippingPolicy')} placeholder="e.g. Ships within 2 business days via USPS Priority Mail..." style={{ minHeight: 80, fontFamily: "'DM Sans',sans-serif", fontSize: 14, padding: 12, borderRadius: 8, border: '1px solid var(--border)', resize: 'vertical', width: '100%', boxSizing: 'border-box' }} />
            </div>
            <div className="form-group">
              <label className="form-label">↩️ Return Policy</label>
              <textarea {...f('returnPolicy')} placeholder="e.g. Returns accepted within 14 days. Item must be in original condition..." style={{ minHeight: 80, fontFamily: "'DM Sans',sans-serif", fontSize: 14, padding: 12, borderRadius: 8, border: '1px solid var(--border)', resize: 'vertical', width: '100%', boxSizing: 'border-box' }} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
            <button type="submit" className="btn btn-cherry" disabled={loading}>
              {loading ? <><span className="spinner" /> Saving...</> : '💾 Save Changes'}
            </button>
          </div>
        </form>
      </div>

      {/* Seller Banner */}
      <div style={{ background: 'white', borderRadius: 14, border: '1px solid var(--border)', padding: 24, maxWidth: 600, marginTop: 24 }}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>🖼️ Storefront Banner</div>
        <div style={{ fontSize: 13, color: 'var(--text-soft)', marginBottom: 16 }}>A banner image shown at the top of your public profile</div>
        {bannerMsg && <div className={`alert ${bannerMsg.startsWith('✅') ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: 12 }}>{bannerMsg}</div>}
        {bannerUrl ? (
          <div>
            <img src={`${API_BASE}${bannerUrl}`} alt="Storefront banner" style={{ width: '100%', height: 130, objectFit: 'cover', borderRadius: 10, border: '1px solid var(--border)', display: 'block' }} />
            <button className="btn btn-ghost btn-sm" style={{ marginTop: 10 }} onClick={async () => {
              await API.patch('/users/me', { ...form, sellerBannerUrl: '' });
              setBannerUrl('');
              setBannerMsg('✅ Banner removed');
              setTimeout(() => setBannerMsg(''), 3000);
            }}>Remove Banner</button>
          </div>
        ) : (
          <label style={{ display: 'block', border: '2px dashed var(--border)', borderRadius: 10, padding: '24px 16px', textAlign: 'center', cursor: 'pointer', background: 'var(--cream)' }}>
            {bannerUploading ? '⏳ Uploading…' : '📎 Click to upload banner image (recommended: 1200×300)'}
            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={async (e) => {
              const file = e.target.files[0];
              if (!file) return;
              setBannerUploading(true);
              setBannerMsg('');
              try {
                const fd = new FormData();
                fd.append('image', file);
                const { data } = await API.post('/upload/listing-image', fd);
                await API.patch('/users/me', { ...form, sellerBannerUrl: data.url });
                setBannerUrl(data.url);
                setBannerMsg('✅ Banner updated');
                setTimeout(() => setBannerMsg(''), 3000);
              } catch { setBannerMsg('Upload failed. Please try again.'); }
              setBannerUploading(false);
            }} />
          </label>
        )}
      </div>

      {/* Bundle Discounts */}
      <div style={{ background: 'white', borderRadius: 14, border: '1px solid var(--border)', padding: 24, maxWidth: 600, marginTop: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 16 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>🎁 Bundle Discounts</div>
            <div style={{ fontSize: 13, color: 'var(--text-soft)' }}>Offer a discount when buyers purchase multiple items from you</div>
          </div>
          <button className={`btn ${bundleActive ? 'btn-cherry' : 'btn-ghost'}`} onClick={async () => {
            setBundleLoading(true);
            if (bundleActive) {
              await API.delete('/users/me/bundle-discount');
              setBundleActive(false);
              setBundleSuccess('Bundle discount removed');
            } else {
              await API.post('/users/me/bundle-discount', bundle);
              setBundleActive(true);
              setBundleSuccess('Bundle discount active!');
            }
            setBundleLoading(false);
            setTimeout(() => setBundleSuccess(''), 3000);
          }} disabled={bundleLoading} style={{ flexShrink: 0 }}>
            {bundleActive ? '✅ Active — Disable' : 'Enable'}
          </button>
        </div>
        {bundleSuccess && <div className="alert alert-success" style={{ marginBottom: 12 }}>{bundleSuccess}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="form-group">
            <label className="form-label">Minimum items</label>
            <input className="form-input" type="number" min="2" max="10" value={bundle.minItems} onChange={e => setBundle(p => ({ ...p, minItems: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Discount %</label>
            <input className="form-input" type="number" min="1" max="50" value={bundle.discountPercent} onChange={e => setBundle(p => ({ ...p, discountPercent: e.target.value }))} />
          </div>
        </div>
        {bundleActive && <div style={{ fontSize: 12, color: '#2D7A3A', marginTop: 8 }}>✅ Buyers get {bundle.discountPercent}% off when purchasing {bundle.minItems}+ items from you</div>}
        {bundleActive && (
          <button className="btn btn-ghost btn-sm" style={{ marginTop: 10 }} onClick={async () => {
            await API.post('/users/me/bundle-discount', bundle);
            setBundleSuccess('Bundle discount updated!');
            setTimeout(() => setBundleSuccess(''), 3000);
          }}>💾 Update Settings</button>
        )}
      </div>

      {/* Auto-Reply */}
      <div style={{ background: 'white', borderRadius: 14, border: '1px solid var(--border)', padding: 24, maxWidth: 600, marginTop: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 16 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>💬 Auto-Reply</div>
            <div style={{ fontSize: 13, color: 'var(--text-soft)' }}>
              {autoReply.enabled
                ? 'Auto-reply is on — new buyers will receive your message instantly.'
                : 'Automatically reply to buyers when they send you their first message.'}
            </div>
          </div>
          <button
            className={`btn ${autoReply.enabled ? 'btn-cherry' : 'btn-ghost'}`}
            onClick={() => setAutoReply(a => ({ ...a, enabled: !a.enabled }))}
            style={{ flexShrink: 0 }}>
            {autoReply.enabled ? '✅ On — Disable' : 'Enable'}
          </button>
        </div>
        {autoReplySuccess && <div className="alert alert-success" style={{ marginBottom: 12 }}>{autoReplySuccess}</div>}
        <div className="form-group">
          <label className="form-label">Auto-reply message</label>
          <textarea
            className="form-input"
            placeholder="e.g. Thanks for reaching out! I'll get back to you shortly. Feel free to ask any questions about my listings."
            value={autoReply.message}
            onChange={e => setAutoReply(a => ({ ...a, message: e.target.value }))}
            style={{ minHeight: 90, fontFamily: "'DM Sans',sans-serif", fontSize: 14, resize: 'vertical' }}
          />
          <div style={{ fontSize: 12, color: 'var(--text-soft)', marginTop: 4 }}>Sent automatically to buyers on their first message to you</div>
        </div>
        <button
          className="btn btn-ghost btn-sm"
          disabled={autoReplyLoading}
          onClick={async () => {
            setAutoReplyLoading(true);
            setAutoReplySuccess('');
            try {
              await API.patch('/users/me', { ...form, autoReplyEnabled: autoReply.enabled, autoReplyMessage: autoReply.message });
              setAutoReplySuccess(autoReply.enabled ? '✅ Auto-reply saved and active' : '✅ Auto-reply settings saved');
              setTimeout(() => setAutoReplySuccess(''), 4000);
            } catch { setAutoReplySuccess('Failed to save'); }
            setAutoReplyLoading(false);
          }}>
          {autoReplyLoading ? 'Saving...' : '💾 Save Auto-Reply'}
        </button>
      </div>

      {/* Vacation Mode */}
      <div style={{ background: vacation.mode ? '#FFF9E6' : 'white', borderRadius: 14, border: `1px solid ${vacation.mode ? '#F5CBA7' : 'var(--border)'}`, padding: 24, maxWidth: 600, marginTop: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>🏖️ Vacation Mode</div>
            <div style={{ fontSize: 13, color: 'var(--text-soft)' }}>
              {vacation.mode ? 'Your listings are paused. Buyers cannot purchase while vacation mode is on.' : 'Pause all your listings while you\'re away. They\'ll be restored when you return.'}
            </div>
          </div>
          <button
            className={`btn ${vacation.mode ? 'btn-cherry' : 'btn-ghost'}`}
            onClick={handleVacationToggle}
            disabled={vacationLoading}
            style={{ flexShrink: 0 }}>
            {vacationLoading ? 'Updating...' : vacation.mode ? '✅ On — Turn Off' : 'Turn On'}
          </button>
        </div>
        {vacationSuccess && <div className="alert alert-success" style={{ marginTop: 12 }}>{vacationSuccess}</div>}
        <div style={{ marginTop: 16 }}>
          <label className="form-label">Message to buyers (optional)</label>
          <input
            className="form-input"
            placeholder="e.g. Back on June 20th. Messages welcome!"
            value={vacation.message}
            onChange={e => setVacation(v => ({ ...v, message: e.target.value }))}
          />
          <div style={{ fontSize: 12, color: 'var(--text-soft)', marginTop: 4 }}>Shown on your listings while vacation mode is active</div>
        </div>
      </div>
    </div>
  );
}
